import { Trash } from "@ignite-analytics/icons";
import { Box, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

type Props = {
    name: string;
    sizeBytes?: number;
    analyzing?: boolean;
    onOpen: () => void;
    onRemove?: () => void;
    removing?: boolean;
};

export const DocumentCard: React.FC<Props> = ({ name, sizeBytes, analyzing, onOpen, onRemove, removing }) => {
    const { formatMessage, formatNumber } = useIntl();
    const dot = name.lastIndexOf(".");
    const extension =
        dot > 0
            ? name
                  .slice(dot + 1)
                  .toUpperCase()
                  .slice(0, 4)
            : "";
    const kilobytes = sizeBytes === undefined ? 0 : Math.max(1, Math.round(sizeBytes / 1_000));
    const size =
        sizeBytes === undefined
            ? undefined
            : kilobytes < 1_000
              ? formatNumber(kilobytes, { style: "unit", unit: "kilobyte" })
              : formatNumber(sizeBytes / 1_000_000, { style: "unit", unit: "megabyte", maximumFractionDigits: 1 });
    return (
        <Box
            sx={{
                position: "relative",
                "&:hover .remove-file, &:focus-within .remove-file": { opacity: 1 },
                "@media (hover: none)": { "& .remove-file": { opacity: 1 } },
            }}
        >
            <Box
                component="button"
                type="button"
                onClick={onOpen}
                sx={{
                    width: 180,
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    textAlign: "left",
                    font: "inherit",
                    color: "inherit",
                    border: 1,
                    borderColor: "tokens.border.border200",
                    borderRadius: "8px",
                    backgroundColor: "tokens.background.backgroundPrimaryBase",
                    cursor: "pointer",
                    transition: "border-color 150ms, box-shadow 150ms",
                    "&:hover": { borderColor: "tokens.border.border300", boxShadow: 1 },
                    "&:focus-visible": { outline: 2, outlineColor: "tokens.icon.iconAccent", outlineOffset: 2 },
                }}
            >
                <Box
                    sx={{
                        height: 80,
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "tokens.background.backgroundSecondary",
                        borderBottom: 1,
                        borderColor: "tokens.border.border200",
                    }}
                >
                    {extension && (
                        <Typography
                            variant="textXs"
                            aria-hidden
                            sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: "6px",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                color: "text.textOnColor",
                                backgroundColor: extension === "PDF" ? "tokens.icon.iconError" : "tokens.icon.iconGray",
                            }}
                        >
                            {extension}
                        </Typography>
                    )}
                </Box>
                <Stack spacing={0.25} sx={{ px: 1.5, py: 1.25, minWidth: 0, width: "100%" }}>
                    <Typography variant="textSm" noWrap sx={{ fontWeight: 500 }}>
                        {name}
                    </Typography>
                    {analyzing ? (
                        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                            <CircularProgress size={10} />
                            <Typography variant="textXs" sx={{ color: "text.textHelper" }}>
                                <FormattedMessage defaultMessage="Analyzing…" />
                            </Typography>
                        </Stack>
                    ) : (
                        size && (
                            <Typography variant="textXs" sx={{ color: "text.textHelper" }}>
                                {size}
                            </Typography>
                        )
                    )}
                </Stack>
            </Box>
            {onRemove && (
                <IconButton
                    size="small"
                    disabled={removing}
                    aria-label={formatMessage({ defaultMessage: "Remove file" })}
                    onClick={onRemove}
                    className="remove-file"
                    sx={{ position: "absolute", top: 4, right: 4, opacity: 0, transition: "opacity 150ms" }}
                >
                    <Trash fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};
